import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { OPERATING_KEYS, MONTH_SHORT } from '../constants/wfReportConfig';
import WfWorksheet from './WfWorksheet';
import './WfReport.css';

const api = axios.create({ baseURL: '/api/wf-report' });
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => (Number(n) || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (v) => (v == null ? '–' : `${Number(v).toFixed(2)}%`);
const prevMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const shiftMonth = (m, n) => { let [y, mm] = m.split('-').map(Number); mm += n; while (mm > 12) { mm -= 12; y++; } while (mm < 1) { mm += 12; y--; } return `${y}-${String(mm).padStart(2, '0')}`; };
const OP_LABELS = { events_private: 'Private events executed', events_wedding: 'Weddings executed', events_corporate: 'Corporate events executed',
  enquiries: 'Enquiries received', site_visits: 'Qualified site visits', bookings_wedding: 'Confirmed — weddings',
  bookings_corporate: 'Confirmed — corporate', bookings_private: 'Confirmed — private', sales_value: 'Sales value captured (RM)' };

function NumInput({ value, onChange, disabled, className = '' }) {
  return <input className={`wf-in ${className}`} inputMode="decimal" value={value ?? ''} disabled={disabled}
    onChange={e => onChange(e.target.value.replace(/[^0-9.-]/g, ''))} />;
}

export default function WfReport() {
  const [month, setMonth] = useState(prevMonth);
  const [view, setView] = useState(null);
  const [inputs, setInputs] = useState({ snapshot: '', pnl: {}, operating: {}, marketing: { spend: '', done_deals: [] } });
  const [vis, setVis] = useState({});
  const [months, setMonths] = useState([]);
  const [holders, setHolders] = useState([]);
  const [showHolders, setShowHolders] = useState(false);
  const [save, setSave] = useState({ state: 'idle', msg: '' });
  const [dlError, setDlError] = useState('');
  const [mode, setMode] = useState('month');            // 'month' editor or read-only 'year' grid
  const [gridYear, setGridYear] = useState(() => Number(prevMonth().slice(0, 4)));
  const worksheetRef = useRef(null);
  const loadSeq = useRef(0);
  const timer = useRef(null);
  const skipNext = useRef(false);
  const pendingRef = useRef(null); // { month, inputs } most recently scheduled but not yet saved
  const visTimer = useRef(null);
  const pendingVisRef = useRef(null); // { month, vis } most recently scheduled but not yet saved
  const saveInFlight = useRef(null);
  const visInFlight = useRef(null);
  const final = view?.status === 'final';

  const load = useCallback(async (m) => {
    const seq = ++loadSeq.current;
    const [v, ms, hs] = await Promise.all([api.get(`/${m}`, auth()), api.get('/months', auth()), api.get('/shareholders', auth())]);
    if (seq !== loadSeq.current) return;
    skipNext.current = true;
    setView(v.data); setMonths(ms.data); setHolders(hs.data);
    setInputs({ snapshot: v.data.inputs.snapshot || '', pnl: v.data.inputs.pnl || {}, operating: v.data.inputs.operating || {},
      marketing: { spend: v.data.inputs.marketing?.spend ?? '', done_deals: v.data.inputs.marketing?.done_deals || [] } });
    const t = {}; v.data.visibility.rows.filter(r => r.kind === 'forecast').forEach(r => { t[r.month] = { confirmed: r.confirmed, unconfirmed: r.unconfirmed, events: r.events }; });
    setVis(t);
  }, []);
  useEffect(() => { load(month).catch(() => setSave({ state: 'error', msg: 'Could not load this month' })); }, [month, load]);

  // Drain pending edits in order. Keep failed edits for retry and make navigation,
  // finalisation and downloads await the same in-flight save.
  const flushVis = useCallback(() => {
    clearTimeout(visTimer.current);
    if (visInFlight.current) return visInFlight.current;
    const run = async () => {
      let wrote = false;
      while (pendingVisRef.current) {
        const pending = pendingVisRef.current;
        setSave({ state: 'saving', msg: 'Saving forecast…' });
        try {
          await api.put('/visibility', Object.entries(pending.vis).map(([m, r]) => ({ month: m, ...r })), auth());
          const r = await api.get(`/${pending.month}`, auth());
          setView(r.data);
          if (pendingVisRef.current === pending) pendingVisRef.current = null;
          wrote = true;
        } catch (e) { setSave({ state: 'error', msg: e.response?.data?.error || 'Save failed. Retry before leaving.' }); return false; }
      }
      if (wrote) setSave({ state: 'saved', msg: 'Forecast saved' });
      return true;
    };
    visInFlight.current = run().finally(() => { visInFlight.current = null; });
    return visInFlight.current;
  }, []);

  const flushSave = useCallback(() => {
    clearTimeout(timer.current);
    if (saveInFlight.current) return saveInFlight.current;
    const run = async () => {
      if (!(await flushVis())) return false;
      while (pendingRef.current) {
        const pending = pendingRef.current;
        setSave({ state: 'saving', msg: 'Saving…' });
        try {
          const r = await api.put(`/${pending.month}`, pending.inputs, auth());
          setView(r.data);
          if (pendingRef.current === pending) pendingRef.current = null;
        } catch (e) { setSave({ state: 'error', msg: e.response?.data?.error || 'Save failed. Retry before leaving.' }); return false; }
      }
      setSave({ state: 'saved', msg: 'All report changes saved' });
      return true;
    };
    saveInFlight.current = run().finally(() => { saveInFlight.current = null; });
    return saveInFlight.current;
  }, [flushVis]);

  const goToMonth = useCallback(async (m) => {
    if (!(await flushSave())) return;
    setMonth(m);
  }, [flushSave]);

  // autosave inputs (debounced)
  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (!view || final) return;
    pendingRef.current = { month, inputs };
    setSave({ state: 'saving', msg: 'Saving…' });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { flushSave(); }, 800);
    return () => clearTimeout(timer.current);
  }, [inputs]); // eslint-disable-line react-hooks/exhaustive-deps

  // A pending debounced save (P&L/operating/marketing or the visibility grid) must
  // be flushed, not cancelled, if the whole page unmounts mid-debounce (e.g.
  // sidebar navigation) — otherwise the edit is silently lost.
  useEffect(() => () => { flushSave(); }, [flushSave]);

  // Visibility grid: keep local state instant, but debounce the network save so an
  // unthrottled keystroke doesn't fire a PUT + GET per character.
  const setVisField = (monthKey, current, key, v) => {
    const nextEntry = { ...current, [key]: v };
    setVis(prev => {
      const next = { ...prev, [monthKey]: nextEntry };
      pendingVisRef.current = { month, vis: next };
      return next;
    });
    setSave({ state: 'saving', msg: 'Saving…' });
    clearTimeout(visTimer.current);
    visTimer.current = setTimeout(() => { flushVis(); }, 800);
  };

  const setOp = (k, v) => setInputs(s => ({ ...s, operating: { ...s.operating, [k]: v } }));

  const download = async (ext) => {
    setDlError('');
    if (!(await flushSave())) return;
    try {
      const r = await api.get(`/${month}/${ext}`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `WF_Shareholder_Report_${MONTH_SHORT[Number(month.slice(5)) - 1]}_${month.slice(0, 4)}.${ext}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Download failed';
      if (e.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).error; } catch { /* keep default */ } }
      setDlError(msg);
    }
  };
  const pickMonthFromGrid = async (m) => {
    try { if (m === month) await load(m); else setMonth(m); setMode('month'); }
    catch { setSave({ state: 'error', msg: 'Could not load the shareholder report' }); }
  };
  const showReport = async () => {
    if (mode === 'month') return;
    if (worksheetRef.current && !(await worksheetRef.current.flush())) return;
    await pickMonthFromGrid(month);
  };
  const changeYear = async (step) => {
    if (worksheetRef.current && !(await worksheetRef.current.flush())) return;
    setGridYear(y => y + step);
  };
  const showYear = async () => { if (mode === 'year') return; if (!(await flushSave())) return; setGridYear(Number(month.slice(0, 4))); setMode('year'); };
  const toggleFinal = async () => {
    if (!(await flushSave())) return;
    try { const r = await api.post(`/${month}/${final ? 'unlock' : 'finalize'}`, {}, auth()); setView(r.data); const ms = await api.get('/months', auth()); setMonths(ms.data); }
    catch (e) { setSave({ state: 'error', msg: e.response?.data?.error || 'Could not change status' }); }
  };
  const saveHolders = async (list) => {
    if (!(await flushSave())) return;
    try { const r = await api.put('/shareholders', list, auth()); setHolders(r.data); const v = await api.get(`/${month}`, auth()); setView(v.data); }
    catch (e) { setSave({ state: 'error', msg: e.response?.data?.error || 'Could not save shareholders' }); }
  };

  const status = useMemo(() => {
    if (!view || view.month !== month) return [];
    const P = view.pnl, O = view.operating;
    const pnlOk = P.total_income !== 0 || P.total_fixed !== 0;
    const opOk = O.enquiries > 0 || O.events_total > 0;
    const mkOk = O.spend > 0 || (view.done_deal_rows || inputs.marketing.done_deals || []).length > 0;
    const visMissing = view.visibility.rows.filter(r => r.kind === 'forecast' && r.confirmed === 0).map(r => r.label.slice(0, 3));
    return [
      { n: 1, t: 'Title', s: 'auto', e: 'auto' },
      { n: 2, t: 'Snapshot', s: 'auto', e: 'auto' },
      { n: 3, t: 'P&L Overview', s: pnlOk ? 'ok' : 'miss', e: pnlOk ? 'filled' : 'empty' },
      { n: 4, t: 'Operating Update', s: opOk ? 'ok' : 'miss', e: opOk ? 'filled' : 'empty' },
      { n: 5, t: 'Marketing', s: mkOk ? 'ok' : 'miss', e: mkOk ? `${O.bookings_total || 0} deals` : 'empty' },
      { n: 6, t: 'Revenue Visibility', s: visMissing.length ? 'miss' : 'ok', e: visMissing.length ? `${visMissing.join(', ')}?` : 'filled' },
      { n: 7, t: 'Appendix', s: 'auto', e: 'auto' }, { n: 8, t: 'Distribution', s: 'auto', e: 'auto' }, { n: 9, t: 'ROI', s: 'auto', e: 'auto' },
    ];
  }, [view, inputs, month]);

  if (!view || view.month !== month) return <div className="wf-sub">{save.state === 'error' ? save.msg : 'Loading…'}</div>;
  const P = view.pnl, O = view.operating;
  const bookingBased = view.deal_source === 'bookings' || view.deal_source === 'bookings_snapshot';
  const dealRows = view.done_deal_rows || inputs.marketing.done_deals || [];


  return (
    <div className="wf-page">
      <div className="wf-head">
        <div>
          <h1>W Factory <span className={`wf-pill ${view.status}`}>{mode === 'year' ? 'P&L worksheet' : view.status}</span></h1>
          <p className="wf-sub">Wow Factory Event Space Sdn. Bhd. · {mode === 'year' ? `full year ${gridYear}` : `figures for ${view.labels.period_label}`}</p>
        </div>
        <div className="wf-actions">
          <div className="wf-seg" role="tablist" aria-label="view">
            <button role="tab" aria-selected={mode === 'month'} className={mode === 'month' ? 'on' : ''} onClick={showReport}>Shareholder report</button>
            <button role="tab" aria-selected={mode === 'year'} className={mode === 'year' ? 'on' : ''} onClick={showYear}>P&amp;L worksheet</button>
          </div>
          {mode === 'month' ? <>
            <span className={`wf-saved ${save.state}`}>{save.msg || (final ? 'Final — inputs locked' : 'Changes save automatically')}</span>
            <div className="wf-month"><button onClick={() => goToMonth(shiftMonth(month, -1))} aria-label="previous month">‹</button><input type="month" aria-label="Reporting month" value={month} onChange={e => { if (/^\d{4}-\d{2}$/.test(e.target.value)) goToMonth(e.target.value); }} /><button onClick={() => goToMonth(shiftMonth(month, 1))} aria-label="next month">›</button></div>
            <button className="wf-btn" onClick={() => download('pptx')}>Download PPTX</button>
            <button className="wf-btn primary" onClick={() => download('pdf')}>Download PDF</button>
            <button className="wf-btn good" onClick={toggleFinal}>{final ? 'Unlock' : 'Mark Final'}</button>
          </> : <>
            <span className="wf-saved idle">Edit amounts directly · click a month for its report</span>
            <div className="wf-month"><button onClick={() => changeYear(-1)} aria-label="previous year">‹</button><strong>{gridYear}</strong><button onClick={() => changeYear(1)} aria-label="next year">›</button></div>
          </>}
        </div>
      </div>
      {save.state === 'error' && <div className="wf-worksheet-status" role="alert"><span className="wf-error">{save.msg}</span><button className="wf-btn" onClick={flushSave}>Retry save</button></div>}
      {dlError && <p className="wf-error">{dlError}</p>}

      {mode === 'year' && (
        <section className="wf-card">
          <div className="wf-card-head"><span className="wf-slide">Yearly</span><h3>Profit &amp; Loss {gridYear}</h3><span className="wf-help">January–December · all amounts in RM</span></div>
          <WfWorksheet key={gridYear} ref={worksheetRef} year={gridYear} onPickMonth={pickMonthFromGrid} />
        </section>
      )}

      {mode === 'month' && <div className="wf-kpis">
        {[['Revenue', fmt(P.total_income), 'RM'], ['Gross profit', fmt(P.gross_profit), 'RM'], ['Net profit', fmt(P.net_profit), 'RM'],
          ['Net margin', P.total_income > 0 ? pct(P.net_profit / P.total_income * 100) : '–', '']].map(([title, value, unit]) =>
          <div className={`wf-kpi ${title === 'Net profit' ? 'featured' : ''}`} key={title}><span>{title}</span><strong><small>{unit}</small> {value}</strong><span>{view.labels.period_label}</span></div>)}
      </div>}
      <div className="wf" hidden={mode === 'year'}>
        <div className="wf-col">
          <section className="wf-card">
            <div className="wf-card-head"><span className="wf-slide">Slide 2</span><h3>Executive Snapshot</h3><span className="wf-help">generated automatically</span></div>
            <div className="wf-report-pnl">
              <div><span>Revenue</span><strong>RM {fmt(P.total_income)}</strong></div>
              <div><span>Gross margin</span><strong>{pct(P.gross_margin_pct)}</strong></div>
              <div><span>Net profit</span><strong className={P.net_profit < 0 ? 'wf-loss' : ''}>RM {fmt(P.net_profit)}</strong></div>
              <div><span>Net margin</span><strong className={P.net_profit < 0 ? 'wf-loss' : ''}>{P.total_income > 0 ? pct(P.net_profit / P.total_income * 100) : '–'}</strong></div>
            </div>
            <p className="wf-hint">The downloaded report uses the four KPI cards from the reference format.</p>
          </section>

          <section className="wf-card">
            <div className="wf-card-head"><span className="wf-slide">Slides 3 + 7</span><h3>Profit &amp; Loss</h3><span className="wf-help">linked to the P&L worksheet</span></div>
            <div className="wf-report-pnl">
              {[['Total income', P.total_income], ['Total COGS', P.total_cogs], ['Gross profit', P.gross_profit],
                ['Fixed costs', P.total_fixed], ['Net profit', P.net_profit]].map(([title, amount]) =>
                <div className={title === 'Net profit' ? 'net' : ''} key={title}><span>{title}</span><strong className={amount < 0 ? 'wf-loss' : ''}>RM {fmt(amount)}</strong></div>)}
            </div>
            <button className="wf-btn" onClick={showYear} style={{ marginTop: 14 }}>Edit P&amp;L worksheet →</button>
          </section>

          <section className="wf-card">
            <div className="wf-card-head"><span className="wf-slide">Slide 4</span><h3>Operating Update</h3></div>
            <div className="wf-fields">
              {OPERATING_KEYS.map(k => <div className="wf-f" key={k}><label>{OP_LABELS[k]}</label>
                {bookingBased && k.startsWith('bookings_')
                  ? <input className="wf-in full ro" readOnly value={O[k] ?? 0} />
                  : <NumInput className="full" value={inputs.operating[k]} disabled={final} onChange={v => setOp(k, v)} />}
              </div>)}
            </div>
            {bookingBased && <p className="wf-hint">Confirmed bookings come from W Factory JQ and Zen bookings for this report month.</p>}
            <div className="wf-strip">
              <div className="wf-stat"><small>Events executed</small><b>{O.events_total}</b></div>
              <div className="wf-stat"><small>Confirmed bookings</small><b>{O.bookings_total}</b></div>
              <div className="wf-stat"><small>Enquiry → site visit</small><b>{pct(O.enquiry_to_visit_pct)}</b></div>
              <div className="wf-stat"><small>Site visit → booking</small><b>{pct(O.visit_to_booking_pct)}</b></div>
            </div>
          </section>

          <section className="wf-card">
            <div className="wf-card-head"><span className="wf-slide">Slide 5</span><h3>Marketing Performance</h3></div>
            <div className="wf-fields">
              <div className="wf-f"><label>Marketing spend (RM)</label><NumInput className="full" value={inputs.marketing.spend} disabled={final} onChange={v => setInputs(s => ({ ...s, marketing: { ...s.marketing, spend: v } }))} /></div>
              <div className="wf-f"><label>Deals closed</label><input className="wf-in full ro" readOnly value={O.bookings_total} /></div>
              <div className="wf-f"><label>Cost per deal</label><input className="wf-in full ro" readOnly value={O.cost_per_deal == null ? '–' : fmt(O.cost_per_deal)} /></div>
            </div>
            <p className="wf-hint">Deals closed reuses the confirmed bookings above.</p>
            <table className="wf-deals" style={{ marginTop: 12, maxWidth: 560 }}>
              <thead><tr><th className="k">Done deals</th><th className="k">Year</th><th className="k">Month</th><th>Events</th></tr></thead>
              <tbody>{dealRows.map((d, i) => (
                <tr key={i}>
                  <td className="k">{d.segment[0].toUpperCase() + d.segment.slice(1)}</td>
                  <td className="k">{d.year}</td>
                  <td className="k">{d.month ? MONTH_SHORT[Number(d.month) - 1] : '—'}</td>
                  <td>{d.count}</td>
                </tr>))}</tbody>
            </table>
            {bookingBased && <p className="wf-hint">Grouped by event month from W Factory JQ and Zen bookings.</p>}
            <p className="wf-note">{bookingBased ? 'The PDF groups these counts by event year.'
              : `Prints as ${view.done_deals.wedding.concat(view.done_deals.corporate, view.done_deals.private || []).join(' · ') || 'None'}.`}</p>
          </section>

          <section className="wf-card">
            <div className="wf-card-head"><span className="wf-slide">Slide 6</span><h3>Secured Revenue Visibility {view.labels.year}</h3><span className="wf-help">actual months come from each month's P&amp;L · forecast months typed</span></div>
            <table className="wf-vis">
              <thead><tr><th className="k">Month</th><th>Confirmed (RM)</th><th>Unconfirmed (RM)</th><th>Total (RM)</th><th>Events</th></tr></thead>
              <tbody>{view.visibility.rows.map(r => {
                const t = vis[r.month] || { confirmed: r.confirmed, unconfirmed: r.unconfirmed, events: r.events };
                const upd = (k, v) => setVisField(r.month, t, k, v);
                const ro = r.kind === 'actual';
                return (<tr key={r.month} className={ro ? 'wf-actual' : 'wf-forecast'}>
                  <td className="k" style={{ color: 'var(--rmc-ink)' }}>{r.label.slice(0, 3)} <span className="wf-tagm">{ro ? (r.month === month ? 'actual · this report' : 'actual') : 'forecast'}</span></td>
                  <td>{ro ? <span className="wf-in ro" style={{ display: 'inline-block' }}>{fmt(r.confirmed)}</span> : <NumInput value={t.confirmed} disabled={final} onChange={v => upd('confirmed', v)} />}</td>
                  <td>{ro ? <span className="wf-in ro" style={{ display: 'inline-block' }}>—</span> : <NumInput value={t.unconfirmed} disabled={final} onChange={v => upd('unconfirmed', v)} />}</td>
                  <td>{fmt(ro ? r.total : (Number(t.confirmed) || 0) + (Number(t.unconfirmed) || 0))}</td>
                  <td>{ro ? <span className="wf-in sm ro" style={{ display: 'inline-block' }}>{r.events}</span> : <NumInput className="sm" value={t.events} disabled={final} onChange={v => upd('events', v)} />}</td>
                </tr>);
              })}</tbody>
              <tfoot><tr><td className="k">Total</td><td>{fmt(view.visibility.totals.confirmed)}</td><td>{fmt(view.visibility.totals.unconfirmed)}</td><td>{fmt(view.visibility.totals.total)}</td><td>{view.visibility.totals.events}</td></tr></tfoot>
            </table>
          </section>

          <section className="wf-card">
            <div className="wf-card-head"><span className="wf-slide">Slides 8 + 9</span><h3>Profit Distribution &amp; ROI</h3><span className="wf-help">calculated from shareholder settings</span></div>
            <table>
              <thead><tr><th className="k">Shareholder</th><th>%</th><th>{view.labels.short_label} payout</th><th>Cumulative</th><th>ROI</th></tr></thead>
              <tbody>{view.distribution.rows.map((d, i) => (
                <tr key={d.shareholder_id}><td className="k" style={{ color: 'var(--rmc-ink)' }}>{d.name}</td><td>{pct(d.pct)}</td><td>{fmt(d.amount)}</td><td>{fmt(view.roi.rows[i].cumulative)}</td><td>{pct(view.roi.rows[i].roi_pct)}</td></tr>))}</tbody>
              <tfoot><tr><td className="k">Total distributed</td><td>{pct(view.distribution.total_pct)}</td><td>{fmt(view.distribution.total_amount)}</td><td>{fmt(view.roi.total_cumulative)}</td><td></td></tr></tfoot>
            </table>
            <p className="wf-note">Payouts use each shareholder’s percentage in profit months. Cumulative returns cover {view.roi.months} months.</p>
          </section>

          <section className="wf-card">
            <div className="wf-collapsed" onClick={() => setShowHolders(s => !s)}>
              <b>Shareholders</b><span>{holders.length} active · {pct(holders.reduce((t, h) => t + Number(h.pct), 0))} · capital RM {fmt(holders.reduce((t, h) => t + Number(h.capital), 0))}</span><span className="chev">{showHolders ? 'close ▾' : 'edit ▸'}</span>
            </div>
            {showHolders && <HoldersEditor holders={holders} onSave={saveHolders} />}
          </section>
        </div>

        <aside className="wf-rail">
          <div className="wf-card wf-payout"><h4>Shareholder distribution</h4><strong>RM {fmt(view.distribution.total_amount)}</strong><p className="wf-hint">{final ? 'Finalised' : 'Proposed'} · {view.labels.period_label}</p><p className="wf-note">{pct(view.distribution.total_pct)} total allocation from shareholder settings.</p></div>
          <div className="wf-card">
            <h4>Report readiness · {view.labels.period_label}</h4>
            <ul className="wf-slides">{status.map(s => <li key={s.n} className={s.s}><span className="n">{s.n}</span><span className="d" /> {s.t}<em>{s.e}</em></li>)}</ul>
            <p className="wf-hint">Red rows still need you. Download works at any time; Mark Final locks the month and records the payouts.</p>
          </div>
          <div className="wf-card">
            <h4>Months</h4>
            <ul className="wf-months">{months.slice(0, 8).map(m => <li key={m.month} className={m.month === month ? 'cur' : ''} onClick={() => goToMonth(m.month)}>{MONTH_SHORT[Number(m.month.slice(5)) - 1]} {m.month.slice(0, 4)}<span className={`wf-pill ${m.status}`}>{m.status}</span></li>)}</ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function HoldersEditor({ holders, onSave }) {
  const [rows, setRows] = useState(holders.map(h => ({ ...h })));
  useEffect(() => setRows(holders.map(h => ({ ...h }))), [holders]);
  const upd = (i, k, v) => setRows(r => r.map((x, j) => j === i ? { ...x, [k]: v } : x));
  return (
    <div style={{ marginTop: 12 }}>
      <table className="wf-holders">
        <thead><tr><th className="k">Name</th><th>%</th><th>Capital (RM)</th><th>Order</th><th>Active</th></tr></thead>
        <tbody>{rows.map((h, i) => (
          <tr key={h.shareholder_id || `new-${i}`}>
            <td className="k"><input className="wf-in full" style={{ textAlign: 'left' }} value={h.name} onChange={e => upd(i, 'name', e.target.value)} /></td>
            <td><NumInput className="sm" value={h.pct} onChange={v => upd(i, 'pct', v)} /></td>
            <td><NumInput value={h.capital} onChange={v => upd(i, 'capital', v)} /></td>
            <td><NumInput className="sm" value={h.sort_order} onChange={v => upd(i, 'sort_order', v)} /></td>
            <td><input type="checkbox" checked={h.active !== false} onChange={e => upd(i, 'active', e.target.checked)} /></td>
          </tr>))}</tbody>
      </table>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="wf-link" onClick={() => setRows(r => [...r, { name: '', pct: 0, capital: 0, sort_order: r.length + 1, active: true }])}>+ add shareholder</button>
        <button className="wf-btn primary" style={{ marginLeft: 'auto' }} onClick={() => onSave(rows.map(h => ({ id: h.shareholder_id, name: h.name, pct: Number(h.pct), capital: Number(h.capital), bank_details: h.bank_details, sort_order: Number(h.sort_order) || 0, active: h.active !== false })))}>Save shareholders</button>
      </div>
      <p className="wf-hint">Removing a holder deactivates them; past payouts stay in the ledger.</p>
    </div>
  );
}
